import type { DaySeparator, Message } from '../api/types'
import { dayMoment } from './format'
import { buchTag, heuteSchluessel, type Uhr } from './ortszeit'

/**
 * The chapter rule of the browser's book (#113, 1.85.0) -- the same rule
 * `Content::chapters()` applies on the server, as a pure function so that
 * `buchreihenfolge.ts` can hold it without a browser.
 *
 * For every post: the separator that opens a chapter at it (or null), and
 * the day line to print above it (or null). The day line stands where the
 * posting day changes -- **except** inside a chapter on the separator's own
 * day: there the chapter line already says the day. A forgotten separator
 * lets the days show again instead of folding a week into one chapter; a
 * trip without separators reads exactly as before.
 *
 * Two posts of one day in two chapters each get their day considered
 * anew: the cut is at the post, so the day line's "has it changed" starts
 * over with every chapter.
 *
 * Every post's day is its local day (1.87.0): its own measured offset, else
 * the trip's zone course for its moment -- the book follows the journey,
 * and a post written in Boston stands under the day it was in Boston.
 */
export function buchGliederung(
  beitraege: Message[],
  verlauf: Uhr,
): { trenner: DaySeparator | null; tagZeile: string | null }[] {
  let kapitelTag: string | null = null
  let letzterTag: string | null = null
  return beitraege.map((nachricht) => {
    // A deleted post prints nothing (`Beitrag` returns null), so it opens no
    // day line either. A separator is a post of its own since #130 and goes
    // when it is deleted; nothing hands it on any more.
    if (nachricht.deleted) return { trenner: null, tagZeile: null }
    const trenner = nachricht.kind === 'broadcast' ? nachricht.day_separator ?? null : null
    if (trenner !== null) {
      kapitelTag = trenner.day
      letzterTag = null
    }
    // **The day a post stands under** (#130): an inserted post takes the day
    // of the post it was put next to (`placed_at`); its own time says when
    // it was written.
    const am = dayMoment(nachricht)
    const uhr = nachricht.tz_offset_min ?? verlauf
    const tag = buchTag(am, uhr)
    const neuerTag = letzterTag !== tag
    letzterTag = tag
    const amTagDesTrenners = kapitelTag !== null && heuteSchluessel(uhr, am) === kapitelTag
    return { trenner, tagZeile: neuerTag && !amTagDesTrenners ? tag : null }
  })
}
